import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { format, parseISO, isToday, isTomorrow, startOfDay } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import RecruiterLayout from '../components/RecruiterLayout';
import TaskComposer from '../components/TaskComposer';
import { Card, CardContent } from '../components/ui/card';
import { Check, X, ListTodo, Clock, User } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const formatDue = (str) => {
  if (!str) return null;
  try {
    const d = parseISO(str);
    if (isToday(d)) return 'Today';
    if (isTomorrow(d)) return 'Tomorrow';
    return format(d, 'EEE, MMM d');
  } catch { return str; }
};
const isOverdue = (str) => {
  if (!str) return false;
  try { return startOfDay(parseISO(str)) < startOfDay(new Date()) && !isToday(parseISO(str)); } catch { return false; }
};

const RecruiterTasks = () => {
  const { userProfile } = useAuth();
  const [todoItems, setTodoItems] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [candidates, setCandidates] = useState([]);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    if (!userProfile?.id) return;
    axios.get(`${API}/api/todos`, { params: { userId: userProfile.id } })
      .then((r) => setTodoItems(Array.isArray(r.data?.todos) ? r.data.todos : []))
      .catch(() => setTodoItems([]))
      .finally(() => setLoading(false));
  }, [userProfile?.id]);

  useEffect(() => {
    if (!userProfile?.organizationId) return;
    axios.get(`${API}/api/positions/organization/${userProfile.organizationId}/candidates`)
      .then((r) => {
        const list = r.data?.data || r.data?.candidates || (Array.isArray(r.data) ? r.data : []);
        setCandidates((Array.isArray(list) ? list : []).map((c) => ({
          applicationId: c.applicationId || c.id,
          name: `${c.firstName || ''} ${c.lastName || ''}`.trim() || c.email || 'Candidate',
          role: c.positionTitle || '',
        })).filter((c) => c.applicationId));
      })
      .catch(() => setCandidates([]));
  }, [userProfile?.organizationId]);

  const saveTodos = (items) => {
    setTodoItems(items);
    if (!userProfile?.id) return;
    axios.put(`${API}/api/todos`, { todos: items }, { params: { userId: userProfile.id } })
      .catch((err) => console.warn('Failed to save tasks:', err));
  };

  const toggleTodo = (id) => saveTodos(todoItems.map((t) => (t.id === id ? { ...t, completed: !t.completed } : t)));
  const removeTodo = (id) => saveTodos(todoItems.filter((t) => t.id !== id));

  const filtered = todoItems.filter((t) => (filter === 'active' ? !t.completed : filter === 'completed' ? t.completed : true));
  const activeCount = todoItems.filter((t) => !t.completed).length;
  const doneCount = todoItems.filter((t) => t.completed).length;

  const tabs = [
    { label: `All (${todoItems.length})`, active: filter === 'all', onClick: () => setFilter('all') },
    { label: `Active (${activeCount})`, active: filter === 'active', onClick: () => setFilter('active') },
    { label: `Completed (${doneCount})`, active: filter === 'completed', onClick: () => setFilter('completed') },
  ];

  return (
    <RecruiterLayout title="Tasks" subtitle="Your personal to-do list, synced with your dashboard" tabs={tabs}>
      <div className="max-w-3xl mx-auto p-4 lg:p-6" data-testid="recruiter-tasks-page">
        <Card className="border-slate-200 shadow-sm rounded-xl overflow-visible">
          <CardContent className="p-2 sm:p-3">
            <TaskComposer candidates={candidates} onCreate={(item) => saveTodos([...todoItems, item])} />

            <div className="mt-1">
              {loading ? (
                <p className="text-sm text-slate-400 py-10 text-center">Loading tasks…</p>
              ) : filtered.length === 0 ? (
                <div className="text-center py-12 text-slate-400 flex flex-col items-center gap-2" data-testid="tasks-empty">
                  <span className="w-12 h-12 rounded-full bg-slate-50 flex items-center justify-center"><ListTodo className="w-6 h-6 text-slate-300" /></span>
                  <p className="text-sm">{filter === 'completed' ? 'No completed tasks yet.' : filter === 'active' ? 'No active tasks — nice work!' : 'No tasks yet. Add one above to get started.'}</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {filtered.map((item) => (
                    editingId === item.id ? (
                      <div key={item.id} className="py-2" data-testid={`task-edit-${item.id}`}>
                        <TaskComposer
                          candidates={candidates}
                          initial={item}
                          onSave={(u) => { saveTodos(todoItems.map((x) => (x.id === u.id ? u : x))); setEditingId(null); }}
                          onCancelEdit={() => setEditingId(null)}
                        />
                      </div>
                    ) : (
                    <div key={item.id} className="flex items-start gap-3 py-3 px-2 group" data-testid={`task-item-${item.id}`}>
                      <button
                        onClick={() => toggleTodo(item.id)}
                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${item.completed ? 'bg-blue-500 border-blue-500 text-white' : 'border-slate-300 hover:border-blue-400'}`}
                        data-testid={`task-toggle-${item.id}`}
                        aria-label={item.completed ? 'Mark as active' : 'Mark as done'}
                      >
                        {item.completed && <Check className="w-3 h-3" />}
                      </button>
                      <button type="button" onClick={() => setEditingId(item.id)} className="flex-1 min-w-0 text-left" data-testid={`task-open-${item.id}`}>
                        <p className={`text-sm leading-relaxed ${item.completed ? 'text-slate-400 line-through' : 'text-slate-800'}`}>{item.text}</p>
                        {item.details && <p className={`text-xs mt-0.5 ${item.completed ? 'text-slate-300' : 'text-slate-500'}`}>{item.details}</p>}
                        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                          {item.dueDate && (
                            <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded-full border ${isOverdue(item.dueDate) && !item.completed ? 'bg-rose-50 text-rose-600 border-rose-200' : 'bg-slate-50 text-slate-500 border-slate-200'}`} data-testid={`task-due-${item.id}`}>
                              <Clock className="w-3 h-3" /> {formatDue(item.dueDate)}
                            </span>
                          )}
                          {(item.mentions || []).map((m, i) => (
                            <span key={i} className="inline-flex items-center gap-1 text-[10px] bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded-full" data-testid={`task-mention-${item.id}-${i}`}>
                              <User className="w-2.5 h-2.5" /> {m.name}
                            </span>
                          ))}
                          {(item.companyTags || []).map((c, i) => (
                            <span key={i} className="text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded-full">@{c.name}</span>
                          ))}
                        </div>
                      </button>
                      <button onClick={() => removeTodo(item.id)} className="text-slate-300 hover:text-red-500 flex-shrink-0 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity" data-testid={`task-delete-${item.id}`} aria-label="Delete task">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    )
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </RecruiterLayout>
  );
};

export default RecruiterTasks;
